// Documents service. Fase B: metadatos reales vía Supabase (RLS por tenant);
// sin variables de entorno usa fixtures (demo/CI). Los binarios viven en
// R2/B2 — aquí solo llegan metadatos (object_key, hash, tamaño), nunca el blob.
import type { DocumentStatus } from '@/types/domain';
import { supabase, hasBackend } from '@/lib/supabase';
import { vaultService } from './vault';
import { loadFailed } from './loadError';

export interface DocumentDetail {
  id: string;
  title: string;
  client: string;
  /** Texto visible del año ('2025' o '—'). Deriva de documents.tax_year
   *  (columna real, VLT-264); '—' = sin año, jamás se inventa. */
  fiscalYear: string;
  sizeLabel: string;
  mime: string;
  hash: string;
  source: string;
  uploadedAt: string;
  status: DocumentStatus;
  /** VLT-362: veredicto del staff (metadata.review) — la transición es REAL. */
  review?: { outcome: 'verified' | 'rejected'; at: string } | null;
  /** VLT-369: key del vault cuando los bytes REALES están guardados. */
  vaultKey?: string | null;
  /** VLT-381: precio en centavos para desbloquear la descarga (0 = libre).
   *  El candado real vive en el Worker (verifica el pago contra Stripe). */
  paywallCents?: number;
}

export interface DocumentsService {
  getDocument(id: string): Promise<DocumentDetail | null>;
  search(query: string): Promise<DocumentDetail[]>;
  list(filter?: { query?: string; status?: DocumentStatus | 'all' }): Promise<DocumentDetail[]>;
  /** VLT-362: aprueba (verified) o pide reemplazo (rejected) — cambia el
   *  estado REAL en la base (el trigger de auditoría lo registra solo). */
  review(id: string, outcome: 'verified' | 'rejected'): Promise<boolean>;
  /** VLT-369: el staff sube el ENTREGABLE (PDF) de un cliente — bytes al
   *  vault + fila en documents; el cliente lo descarga desde /downloads. */
  uploadDeliverable(clientId: string, title: string, file: File, paywallCents?: number): Promise<boolean>;
  /** VLT-385: el CLIENTE añade un documento a su expediente sin solicitud —
   *  vault + RPC de contrato cerrado (0021); queda 'pending_review'. */
  registerOwn(file: File): Promise<{ ok: boolean; errorKey?: string }>;
  /** VLT-264: asigna (o quita con null) el año fiscal — mueve el documento
   *  de carpeta en el vault. Escribe la columna REAL documents.tax_year. */
  setTaxYear(id: string, year: number | null): Promise<boolean>;
}

const FIXTURES: DocumentDetail[] = [
  { id: '1', title: "Driver's License — M. Santos", client: 'María Santos', fiscalYear: '—', sizeLabel: '1.1 MB', mime: 'image/jpeg', hash: 'sha256:41bb…09ce', source: 'Enlace seguro', uploadedAt: '2026-07-16 08:44', status: 'expiring_soon' },
  { id: '2', title: 'Passport — A. Rivera', client: 'Andrés Rivera', fiscalYear: '—', sizeLabel: '2.0 MB', mime: 'image/jpeg', hash: 'sha256:8c1f…77aa', source: 'App móvil', uploadedAt: '2026-07-15 17:02', status: 'expiring_soon' },
  { id: '3', title: 'Residence Card — L. Gómez', client: 'Lucía Gómez', fiscalYear: '—', sizeLabel: '0.9 MB', mime: 'image/png', hash: 'sha256:c04d…21b7', source: 'Portal del cliente', uploadedAt: '2026-07-14 11:20', status: 'expiring_soon' },
  { id: '4', title: 'W-2 2025 — Núñez LLC', client: 'Núñez LLC', fiscalYear: '2025', sizeLabel: '2.4 MB', mime: 'application/pdf', hash: 'sha256:9f2c…d81a', source: 'Portal del cliente', uploadedAt: '2026-07-16 09:12', status: 'expired' },
  { id: '5', title: '1099-NEC Freelance.pdf', client: 'Diego Fuentes', fiscalYear: '2025', sizeLabel: '0.8 MB', mime: 'application/pdf', hash: 'sha256:77aa…f3e2', source: 'App móvil', uploadedAt: '2026-07-16 07:03', status: 'active' },
  // VLT-381: entregable con paywall — descargar exige pagar (demo simula el pago)
  { id: '6', title: 'Declaración 2025 — entrega final.pdf', client: 'María Santos', fiscalYear: '2025', sizeLabel: '1.6 MB', mime: 'application/pdf', hash: 'sha256:a1d2…44fe', source: 'Entregable', uploadedAt: '2026-07-17 10:05', status: 'active', paywallCents: 500 },
];

// Registro extendido determinístico (Fase A) — suficiente volumen para probar
// paginación de PDF y rendimiento de la tabla.
const NAMES = ['María Santos', 'Andrés Rivera', 'Lucía Gómez', 'Núñez LLC', 'Kwame Osei', 'Diego Fuentes', 'Innova LLC', 'Rosa Peralta'];
const KINDS = ['W-2 2025', '1099-NEC 2025', 'Pasaporte', 'Licencia de conducir', 'Estado bancario', 'Carta poder', '1098-T 2025', 'Social Security card'];
const STATUSES: DocumentStatus[] = ['active', 'active', 'expiring_soon', 'active', 'expired', 'active', 'archived', 'active'];
const GENERATED: DocumentDetail[] = Array.from({ length: 32 }, (_, i) => ({
  id: `g${i + 1}`,
  title: `${KINDS[i % KINDS.length]} — ${NAMES[(i * 3) % NAMES.length]}`,
  client: NAMES[(i * 3) % NAMES.length],
  fiscalYear: i % 3 === 0 ? '2025' : i % 3 === 1 ? '2024' : '—',
  sizeLabel: `${(0.4 + (i % 9) * 0.3).toFixed(1)} MB`,
  mime: i % 4 === 0 ? 'image/jpeg' : 'application/pdf',
  hash: `sha256:${(i + 11).toString(16).padStart(4, '0')}…${(i * 97).toString(16).padStart(4, '0')}`,
  source: ['Portal del cliente', 'Enlace seguro', 'App móvil'][i % 3],
  uploadedAt: `2026-07-${String((i % 15) + 1).padStart(2, '0')} ${String(8 + (i % 10))}:${String((i * 7) % 60).padStart(2, '0')}`,
  status: STATUSES[i % STATUSES.length],
}));
const ALL = [...FIXTURES, ...GENERATED];

// ─── Implementación real (Supabase, RLS por tenant) ─────────────────────────

interface DocRow {
  id: string;
  title: string;
  status: string;
  tax_year: number | null;
  object_key: string | null;
  expiry_date: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  checksum_sha256: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  clients: { first_name: string; last_name: string } | null;
}

const SOON_DAYS = 30;

/** Estados de la BD (uploaded/pending_review/verified/…) → estados de la UI. */
const toUiStatus = (row: DocRow): DocumentStatus => {
  if (row.status === 'expired') return 'expired';
  if (row.status === 'archived') return 'archived';
  if (row.expiry_date) {
    const days = (new Date(row.expiry_date).getTime() - Date.now()) / 86_400_000;
    if (days < 0) return 'expired';
    if (days <= SOON_DAYS) return 'expiring_soon';
  }
  return 'active';
};

const sizeLabel = (bytes: number | null): string =>
  bytes == null ? '—' : bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const rowToDetail = (r: DocRow): DocumentDetail => ({
  id: r.id,
  title: r.title,
  client: r.clients ? `${r.clients.first_name} ${r.clients.last_name}`.trim() : '—',
  fiscalYear: r.tax_year != null ? String(r.tax_year) : ((r.metadata?.fiscal_year as string | undefined) ?? '—'),
  sizeLabel: sizeLabel(r.size_bytes),
  mime: r.mime_type ?? '—',
  hash: r.checksum_sha256 ? `sha256:${r.checksum_sha256.slice(0, 4)}…${r.checksum_sha256.slice(-4)}` : '—',
  source: (r.metadata?.source as string | undefined) ?? 'Portal',
  uploadedAt: r.created_at.slice(0, 16).replace('T', ' '),
  status: toUiStatus(r),
  review: isReview(r.metadata?.review) ? r.metadata.review : null,
  // Solo keys reales del vault (u/<uid>/<uuid>) habilitan la descarga.
  vaultKey: r.object_key && /^u\/[0-9a-f-]{36}\/[0-9a-f-]{36}$/.test(r.object_key) ? r.object_key : null,
  paywallCents: (() => {
    const c = (r.metadata?.paywall as { amountCents?: unknown } | undefined)?.amountCents;
    return Number.isInteger(c) && (c as number) > 0 ? (c as number) : 0;
  })(),
});

const isReview = (v: unknown): v is { outcome: 'verified' | 'rejected'; at: string } =>
  !!v && typeof v === 'object' && 'outcome' in v && 'at' in v;

const fetchDocs = async (): Promise<DocumentDetail[]> => {
  const { data, error } = await supabase
    .from('documents')
    .select('id, title, status, tax_year, expiry_date, mime_type, size_bytes, checksum_sha256, object_key, metadata, created_at, clients(first_name, last_name)')
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(500);
  if (error) loadFailed('documents.list', error);
  return (data as unknown as DocRow[]).map(rowToDetail);
};

const realDocuments: DocumentsService = {
  async getDocument(id) {
    const { data, error } = await supabase
      .from('documents')
      .select('id, title, status, tax_year, expiry_date, mime_type, size_bytes, checksum_sha256, object_key, metadata, created_at, clients(first_name, last_name)')
      .eq('id', id)
      .maybeSingle();
    if (error) loadFailed('documents.get', error);
    if (!data) return null;
    return rowToDetail(data as unknown as DocRow);
  },
  async search(query) {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return (await fetchDocs()).filter((d) => `${d.title} ${d.client}`.toLowerCase().includes(q)).slice(0, 5);
  },
  async list({ query = '', status = 'all' } = {}) {
    const q = query.trim().toLowerCase();
    return (await fetchDocs()).filter(
      (d) =>
        (status === 'all' || d.status === status) &&
        (!q || `${d.title} ${d.client}`.toLowerCase().includes(q)),
    );
  },
  async uploadDeliverable(clientId, title, file, paywallCents) {
    const up = await vaultService.upload(file);
    if (!up.ok) throw new Error(up.errorKey);
    const { data: orgId, error: orgError } = await supabase.rpc('current_org_id');
    if (orgError || !orgId) return false;
    const { error } = await supabase.from('documents').insert({
      organization_id: orgId as string,
      client_id: clientId,
      title,
      object_key: up.result.key,
      storage_bucket: 'vaultex-documents-staging',
      mime_type: file.type || 'application/pdf',
      size_bytes: up.result.size,
      checksum_sha256: up.result.sha256,
      status: 'verified',
      metadata: {
        source: 'deliverable',
        // VLT-381: precio de recuperación en centavos ENTEROS (regla §37)
        ...(Number.isInteger(paywallCents) && paywallCents! > 0 ? { paywall: { amountCents: paywallCents } } : {}),
      },
    } as never);
    if (error) {
      // VLT-074 fase B: el trigger de la base rebota con estado con nombre —
      // la clave i18n viaja como mensaje y el caller la traduce en el toast.
      if (error.message.includes('limite_almacenamiento')) throw new Error('limits.storage');
      console.warn('[VaulTex] documents.uploadDeliverable:', error.message);
      return false;
    }
    return true;
  },

  async registerOwn(file) {
    const up = await vaultService.upload(file);
    if (!up.ok) return { ok: false, errorKey: up.errorKey };
    const { error } = await supabase.rpc('register_own_document', {
      p_object_key: up.result.key,
      p_title: file.name.slice(0, 200),
      p_mime: file.type || 'application/octet-stream',
      p_size_bytes: up.result.size,
      p_sha256: up.result.sha256,
    } as never);
    if (error) {
      console.warn('[VaulTex] documents.registerOwn:', error.message);
      return { ok: false, errorKey: 'errors.generic.what' };
    }
    return { ok: true };
  },

  async setTaxYear(id, year) {
    const { error } = await supabase
      .from('documents')
      .update({ tax_year: year } as never)
      .eq('id', id);
    if (error) {
      console.warn('[VaulTex] documents.setTaxYear:', error.message);
      return false;
    }
    return true;
  },

  async review(id, outcome) {
    // Leer-fusionar-escribir: si la lectura falla, NO se escribe (pisaría las
    // demás llaves de metadata con un objeto vacío).
    const { data: cur, error: readErr } = await supabase.from('documents').select('metadata').eq('id', id).maybeSingle();
    if (readErr || !cur) return false;
    const meta = (cur as { metadata: Record<string, unknown> } | null)?.metadata ?? {};
    const { error } = await supabase
      .from('documents')
      .update({
        status: outcome,
        metadata: { ...meta, review: { outcome, at: new Date().toISOString() } },
      } as never)
      .eq('id', id);
    if (error) {
      console.warn('[VaulTex] documents.review:', error.message);
      return false;
    }
    return true;
  },
};

// ─── Implementación demo (fixtures, Fase A) ──────────────────────────────────

const fixtureDocuments: DocumentsService = {
  getDocument: (id) => Promise.resolve(ALL.find((d) => d.id === id) ?? null),
  search: (query) => {
    const q = query.trim().toLowerCase();
    if (!q) return Promise.resolve([]);
    return Promise.resolve(
      ALL.filter((d) => `${d.title} ${d.client}`.toLowerCase().includes(q)).slice(0, 5),
    );
  },
  list: ({ query = '', status = 'all' } = {}) => {
    const q = query.trim().toLowerCase();
    return Promise.resolve(
      ALL.filter(
        (d) =>
          (status === 'all' || d.status === status) &&
          (!q || `${d.title} ${d.client}`.toLowerCase().includes(q)),
      ),
    );
  },
  uploadDeliverable: (clientId, title, file, paywallCents) => {
    ALL.unshift({
      id: `dl${Date.now()}`, title, client: clientId, fiscalYear: '2025',
      sizeLabel: `${Math.max(0.1, file.size / 1024 / 1024).toFixed(1)} MB`,
      mime: file.type || 'application/pdf', hash: 'sha256:demo…demo',
      source: 'Entregable', uploadedAt: new Date().toISOString().slice(0, 16).replace('T', ' '),
      status: 'active', vaultKey: null, paywallCents: paywallCents ?? 0,
    });
    return Promise.resolve(true);
  },
  registerOwn: (file) => {
    ALL.unshift({
      id: `own${Date.now()}`, title: file.name, client: 'María Santos', fiscalYear: '2025',
      sizeLabel: `${Math.max(0.1, file.size / 1024 / 1024).toFixed(1)} MB`,
      mime: file.type || 'application/pdf', hash: 'sha256:demo…demo',
      source: 'Expediente del cliente', uploadedAt: new Date().toISOString().slice(0, 16).replace('T', ' '),
      status: 'active', vaultKey: null,
    });
    return Promise.resolve({ ok: true });
  },
  review: (id, outcome) => {
    const d = ALL.find((x) => x.id === id);
    if (!d) return Promise.resolve(false);
    d.review = { outcome, at: new Date().toISOString() };
    return Promise.resolve(true);
  },
  setTaxYear: (id, year) => {
    const d = ALL.find((x) => x.id === id);
    if (!d) return Promise.resolve(false);
    d.fiscalYear = year == null ? '—' : String(year);
    return Promise.resolve(true);
  },
};

export const documentsService: DocumentsService = hasBackend ? realDocuments : fixtureDocuments;
